import React, { useState } from "react";
import emailjs from "emailjs-com";
import { company, serviceOptions } from "../data/content";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { name: "", company: "", email: "", phone: "", service: "", message: "" };

function Field({ id, label, error, optional, children }) {
  return (
    <div className="field">
      <label htmlFor={id}>
        {label} {optional && <span className="field-optional">(optional)</span>}
      </label>
      {children}
      {error && <div className="field-error" id={`${id}-error`}>{error}</div>}
    </div>
  );
}

function Contact() {
  const [formData, setFormData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = "Please enter your name.";
    if (!formData.email.trim()) next.email = "Please enter your email.";
    else if (!EMAIL_REGEX.test(formData.email.trim())) next.email = "Please enter a valid email address.";
    if (!formData.phone.trim()) next.phone = "Please enter your phone number.";
    if (!formData.message.trim()) next.message = "Please tell us a little about your project.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    if (!validate()) {
      setStatus({ type: "error", message: "Please fix the highlighted fields and try again." });
      return;
    }

    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      setStatus({
        type: "error",
        message:
          "Email is not configured yet. Add REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID and REACT_APP_EMAILJS_PUBLIC_KEY to a .env.local file.",
      });
      return;
    }

    setSending(true);

    const combinedFields = [
      `Email: ${formData.email}`,
      `Phone: ${formData.phone}`,
      `Company: ${formData.company || "-"}`,
      `Service of interest: ${formData.service || "-"}`,
      `Message: ${formData.message}`,
    ].join("\n");

    emailjs
      .send(SERVICE_ID, TEMPLATE_ID, { name: formData.name, message: combinedFields }, PUBLIC_KEY)
      .then(() => {
        setStatus({ type: "success", message: "Thank you. Your message has been sent — our team will be in touch within one business day." });
        setFormData(EMPTY);
      })
      .catch((err) => {
        console.error("EmailJS error:", err);
        setStatus({
          type: "error",
          message: `Sorry, something went wrong sending your message. Please try again or email ${company.email}.`,
        });
      })
      .finally(() => setSending(false));
  };

  const inputProps = (name) => ({
    id: `cf-${name}`,
    name,
    value: formData[name],
    onChange: handleChange,
    className: `input ${errors[name] ? "is-invalid" : ""}`,
    "aria-invalid": errors[name] ? "true" : undefined,
    "aria-describedby": errors[name] ? `cf-${name}-error` : undefined,
  });

  return (
    <section id="contact" className="section section-alt">
      <div className="container">
        <div className="contact-wrap" data-reveal>
          <aside className="contact-info">
            <p className="eyebrow eyebrow-light">Contact</p>
            <h2>Let's discuss your project</h2>
            <p className="contact-info-lead">
              Share a few details and a specialist from our data or development team will get back to you
              within one business day.
            </p>

            <ul className="contact-list">
              <li>
                <i className="fa-solid fa-location-dot" aria-hidden="true" />
                <div>
                  <span className="contact-k">Office</span>
                  {company.address.map((line) => (
                    <span className="contact-v" key={line}>{line}</span>
                  ))}
                </div>
              </li>
              <li>
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <div>
                  <span className="contact-k">Office line</span>
                  <a className="contact-v" href={company.officePhone.href}>{company.officePhone.label}</a>
                </div>
              </li>
              <li>
                <i className="fa-solid fa-headset" aria-hidden="true" />
                <div>
                  <span className="contact-k">Support</span>
                  <a className="contact-v" href={company.thaiSupport.href}>{company.thaiSupport.label}</a>
                </div>
              </li>
              <li>
                <i className="fa-solid fa-envelope" aria-hidden="true" />
                <div>
                  <span className="contact-k">Email</span>
                  <a className="contact-v" href={`mailto:${company.email}`}>{company.email}</a>
                </div>
              </li>
            </ul>
          </aside>

          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="form-row-2">
              <Field id="cf-name" label="Full name" error={errors.name}>
                <input type="text" autoComplete="name" {...inputProps("name")} />
              </Field>
              <Field id="cf-company" label="Company" optional>
                <input type="text" autoComplete="organization" {...inputProps("company")} />
              </Field>
            </div>
            <div className="form-row-2">
              <Field id="cf-email" label="Work email" error={errors.email}>
                <input type="email" autoComplete="email" {...inputProps("email")} />
              </Field>
              <Field id="cf-phone" label="Phone" error={errors.phone}>
                <input type="tel" autoComplete="tel" {...inputProps("phone")} />
              </Field>
            </div>
            <Field id="cf-service" label="Service of interest" optional>
              <select {...inputProps("service")}>
                <option value="">Select a service</option>
                {serviceOptions.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </Field>
            <Field id="cf-message" label="Project details" error={errors.message}>
              <textarea rows="5" {...inputProps("message")} />
            </Field>

            <button type="submit" className="btn-corp btn-corp-primary btn-corp-lg w-100" disabled={sending}>
              {sending ? "Sending…" : "Send enquiry"}
            </button>

            {status.message && (
              <p className={`form-status form-status-${status.type}`} role="status">
                {status.message}
              </p>
            )}
            <p className="form-note">We use your details only to respond to your enquiry.</p>
          </form>
        </div>
      </div>
    </section>
  );
}

export default Contact;
